/**
 * Custom hook for scroll restoration
 *
 * Applies the target position before paint. It re-applies on every `watchKey` change
 * because the section list is not final on the first render: campaign sections are
 * filtered out until their query resolves, so the anchored section may not exist yet.
 * Once the user scrolls themselves we stay out of the way.
 */

import type { RefObject } from 'react';
import { useLayoutEffect, useRef } from 'react';

interface UseScrollRestorationParams {
  containerRef: RefObject<HTMLElement | null>;
  /** Returns null while the anchored section is not in the DOM yet */
  resolveTarget: () => number | null;
  enabled?: boolean;
  /** Changing this re-applies the target - pass an identity of the rendered sections */
  watchKey?: string;
}

export function useScrollRestoration({
  containerRef,
  resolveTarget,
  enabled = true,
  watchKey,
}: UseScrollRestorationParams) {
  const resolveTargetRef = useRef(resolveTarget);
  resolveTargetRef.current = resolveTarget;

  const userTookOverRef = useRef(false);

  useLayoutEffect(() => {
    if (!enabled || userTookOverRef.current) return undefined;

    const container = containerRef.current;
    if (!container) return undefined;

    const target = resolveTargetRef.current();
    if (target !== null) {
      container.scrollTop = target;
    }

    const handOver = () => {
      userTookOverRef.current = true;
    };

    container.addEventListener('wheel', handOver, { passive: true, once: true });
    container.addEventListener('touchstart', handOver, { passive: true, once: true });

    return () => {
      container.removeEventListener('wheel', handOver);
      container.removeEventListener('touchstart', handOver);
    };
  }, [containerRef, enabled, watchKey]);
}
